import React, { useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';

export interface TooltipProps {
    id: string | React.ReactElement;
    content: string;
    index?: string;
    elementId?: string;
    href?: string;
    replacement?: any;
    place?: 'left' | 'top' | 'right' | 'bottom';
    type?: 'dark' | 'success' | 'warning' | 'error' | 'info' | 'light';
    effect: 'solid' | 'float';
    icon?: any;
    empty?: boolean;
    triggerClassName?: string;
    contentClassName?: string;
    triggerDecorationClassName?: string;
    dataAutomationId?: string;
    plain?: boolean;
    multiline?: boolean;
    contentReplacement?: any;
    onClickHandler?: (e: any) => void;
    isIdDynamicText?: boolean;
    linkDomId?: string;
    ariaLabel?: string;
};

/**
 * id - trigger id (used for Formatted message to render trigger text). It is not used for tooltip where trigger is icon;
 * content - content id for Formatted Message or whole html which will be rendered;
 * index - unique identifier to construct unique data-for attributes;
 * replacement - object for trigger value;
 * icon - If this param is passed, tooltip's trigger is not text, but an icon;
 * triggerClassName - custom class names for trigger;
 * contentClassName - custom class names for content;
 * triggerDecorationClassName - by default all tooltips have dotted bottom border;
 * dataAutomationId - optional prop needed for automation QA.
 * plain - when content is a dynamic value, which does not need to be parsed(translated)
 *
 * @param props
 * @returns {*}
 * @constructor
 */
const Tooltip = (props: TooltipProps) => {
    const {
        id,
        elementId=undefined,
        content,
        index,
        href,
        replacement,
        place='top',
        type='dark',
        effect='solid',
        icon=undefined,
        empty=false,
        triggerClassName='',
        contentClassName='',
        triggerDecorationClassName='tooltip-base',
        dataAutomationId=undefined,
        plain=false,
        multiline=false,
        contentReplacement,
        onClickHandler,
        isIdDynamicText=false,
        linkDomId = null,
        ariaLabel = undefined
    } = props;

    const intlApi = useIntl();
    const TOOLTIP_DELAY_HIDE = 200;
    const TOOLTIP_DELAY_SHOW = 300;

    const tRef = useRef(null);

    const onKeyUpEvent = (e) => {
        // check for Esc key
        if (e.keyCode === 27) {
            hideTooltip();
        }
    };

    const hideTooltip = () => {
        ReactTooltip.hide();
    };

    const showTooltip = () => {
        ReactTooltip.hide();
        ReactTooltip.show(tRef.current);
    };

    const gtmHoverEvent = () => {
        // @ts-ignore
        window.dataLayer.push({ 'event': 'hoverEvent', dataAutomationId, elementId, content });
    };

    const uniqueIdentifier = useMemo(() => icon ? `tooltip_${index}` : elementId ? `${elementId}_tooltip_${index}` : `${id}_tooltip_${index}`, [icon, elementId, id, index]);

    const defineLinkPropsIfLink = () => href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {};

    const getTooltipTriggerType = () => {
        if (icon) {
            return (
                <>
                    <FontAwesomeIcon icon={icon} />
                    <span className="usa-sr-only">
                        <FormattedMessage id="common.more.info" />
                    </span>
                </>
            ); // icon, with hidden text
        } else if (plain || React.isValidElement(id) || isIdDynamicText) {
            return id; // dynamic text, no need to parse/translate;
        } else if (!empty && typeof id === 'string') {
            return <FormattedMessage id={id} values={replacement}/>; // i18n id, will be translated;
        }
        return null;
    };

    const tooltipTriggerType =  getTooltipTriggerType();

    return <span className='ga-tooltip-container' onMouseOver = { gtmHoverEvent }>
        <a data-tip
            ref={tRef}
            data-for={uniqueIdentifier}
            { ...defineLinkPropsIfLink() }
            {...(dataAutomationId ? {'data-automation-id': `${dataAutomationId}_tooltip_base`} : {}) }
            className={`text-base cursor-pointer ${triggerClassName} ${triggerDecorationClassName}`}
            onClick={onClickHandler}
            onMouseLeave={() => hideTooltip()}
            {...(linkDomId ? {id: linkDomId} : {}) }
            onFocus={() => showTooltip()}
            onBlur={() => hideTooltip()}
            onKeyUp={(e) => onKeyUpEvent(e)}
            aria-describedby={uniqueIdentifier}
            href="javascript:void(0)"
            aria-label={ariaLabel}
        >
            {tooltipTriggerType}
        </a>
        <span
            className={`gi-tooltip ${contentClassName}`}
            {...(dataAutomationId ? {'data-automation-id': `${dataAutomationId}_tooltip_content`} : {}) }
            onFocus={() => showTooltip()}
            onBlur={() => hideTooltip()}
            onKeyUp={(e) => onKeyUpEvent(e)}>
            <ReactTooltip
                place={place}
                type={type}
                effect={effect}
                className='tooltip-component'
                id={uniqueIdentifier}
                multiline={multiline}
                delayHide={TOOLTIP_DELAY_HIDE}
                delayShow={TOOLTIP_DELAY_SHOW}
                overridePosition={({ left, top }, node) => {
                    const w = window.innerWidth;
                    const h = window.innerHeight;
                    const tooltipWidth = node.offsetWidth;
                    const tooltipHeight = node.offsetHeight;


                    let newLeft = left;
                    if (left < 0) newLeft = 0;
                    else if (left + tooltipWidth > w) newLeft = w - tooltipWidth;


                    let newTop = top;
                    if (top < 0) newTop = 0;
                    else if (top + tooltipHeight > h) newTop = h - tooltipHeight;


                    return { left: newLeft, top: newTop };
                }}
            >
                { typeof content === 'string' && !plain ? parse(`${intlApi.formatMessage({ id: content }, contentReplacement)}`) : content }
            </ReactTooltip>
        </span>
    </span>;
};

Tooltip.propTypes = {
    id: PropTypes.any.isRequired,
    content: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    index: PropTypes.string,
    elementId: PropTypes.string,
    href: PropTypes.string,
    replacement: PropTypes.any,
    place: PropTypes.oneOf(['left', 'top', 'right', 'bottom']),
    type: PropTypes.oneOf(['dark', 'success', 'warning', 'error', 'info', 'light']),
    effect: PropTypes.oneOf(['solid', 'float']),
    icon: PropTypes.any,
    empty: PropTypes.bool,
    triggerClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    triggerDecorationClassName: PropTypes.string,
    dataAutomationId: PropTypes.string,
    plain: PropTypes.bool,
    multiline: PropTypes.bool,
    contentReplacement: PropTypes.any,
    onClickHandler: PropTypes.func
};

Tooltip.defaultProps = {
    elementId: undefined,
    place: 'top',
    type: 'dark',
    effect: 'solid',
    icon: undefined,
    empty: false,
    triggerClassName: '',
    contentClassName: '',
    triggerDecorationClassName: 'tooltip-base',
    dataAutomationId: undefined,
    plain: false,
    multiline: false,
};
export default Tooltip;